/** Bump when regenerating public/og/*.png (keep in sync with server OG_IMG_VERSION). */
export const OG_IMG_VERSION = '20260525140702';

export const OG_BRAND_PATHS = {
  site: '/og/site.png',
  miniwebsite: '/og/miniwebsite.png',
  crm: '/og/crm.png',
} as const;

export type OgBrandKey = keyof typeof OG_BRAND_PATHS;

/** Default homepage rotation pool (sync with config/og-images.json). */
export const DEFAULT_HOMEPAGE_OG_POOL = [
  OG_BRAND_PATHS.site,
  '/assets/brand/official-site-logo.png',
];

export function ogBrandImageUrl(origin: string, key: OgBrandKey): string {
  const base = origin.replace(/\/+$/, '');
  const path = OG_BRAND_PATHS[key];
  return `${base}${path}?v=${OG_IMG_VERSION}`;
}

/** Homepage rotation pool always includes the default site brand image. */
export function buildHomepageOgPool(customImages: string[]): string[] {
  const pool = customImages
    .filter((x) => typeof x === 'string' && String(x).trim())
    .map((x) => String(x).trim());
  const hasSite = pool.some((u) => u.includes('/og/site.png') || u.endsWith('site.png'));
  if (pool.length && !hasSite) pool.unshift(OG_BRAND_PATHS.site);
  return pool;
}

/** DB pool when configured; otherwise default pool so rotation works without admin setup. */
export function resolveHomepageOgPool(homepageSettings: { previews?: { ogImages?: string[] } } | null | undefined): string[] {
  const custom = Array.isArray(homepageSettings?.previews?.ogImages)
    ? homepageSettings!.previews!.ogImages!.filter((x) => typeof x === 'string' && !!String(x).trim())
    : [];
  if (custom.length) return buildHomepageOgPool(custom);
  return buildHomepageOgPool(DEFAULT_HOMEPAGE_OG_POOL);
}

export function pickHomepageOgFromPool(pool: string[], mode: string, origin: string): string {
  if (!pool.length) return ogBrandImageUrl(origin, 'site');
  let pick: string;
  if (mode === 'random') {
    pick = pool[Math.floor(Math.random() * pool.length)];
  } else if (mode === 'sequential') {
    const key = '__ogSeqCursor';
    const g = globalThis as unknown as Record<string, number>;
    if (typeof g[key] !== 'number') g[key] = 0;
    pick = pool[g[key] % pool.length];
    g[key]++;
  } else {
    const today = new Date().toISOString().slice(0, 10);
    let hash = 0;
    for (let i = 0; i < today.length; i++) hash = ((hash << 5) - hash + today.charCodeAt(i)) | 0;
    pick = pool[Math.abs(hash) % pool.length];
  }
  if (/^https?:\/\//i.test(pick)) return appendOgVersion(pick);
  const abs = pick.startsWith('/') ? `${origin.replace(/\/+$/, '')}${pick}` : `${origin.replace(/\/+$/, '')}/${pick}`;
  return appendOgVersion(abs);
}

export function appendOgVersion(url: string): string {
  const u = String(url || '').trim();
  if (!u) return u;
  if (/[?&]v=/.test(u)) return u;
  return `${u}${u.includes('?') ? '&' : '?'}v=${OG_IMG_VERSION}`;
}
